import { taskReducer } from './reducer'
import { diagnosticMessage, validateTaskEvent } from './validate'
import { emptyTaskState, TaskEvent, TaskState } from './types'

type ReplayResponse = { events?: unknown[]; next?: number; cursor?: string; hasMore?: boolean }
type EventSourceFactory = (url: string) => EventSource
export const TASK_NAMESPACE = 'loci-memory-palace-local-v1'

export async function taskIdForThread(threadId: string, namespace = TASK_NAMESPACE) {
  return taskIdForIdentifier(threadId, namespace)
}

export async function taskIdForWorkflow(workflowId: string, namespace = TASK_NAMESPACE) {
  return taskIdForIdentifier(workflowId, namespace)
}

async function taskIdForIdentifier(identifier: string, namespace: string) {
  const input = new TextEncoder().encode(`${namespace}\u0000${identifier}`)
  const digest = await globalThis.crypto.subtle.digest('SHA-256', input)
  const bytes = new Uint8Array(digest)
  return `task_${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`
}

export class TaskRuntime {
  state: TaskState
  private source?: EventSource
  private reconnectTimer?: ReturnType<typeof setTimeout>
  private attempt = 0
  private stopped = true
  private listener?: (state: TaskState) => void

  constructor(public readonly taskId: string, onState?: (state: TaskState) => void) { this.state = emptyTaskState(taskId); this.listener = onState }
  private commit(next: TaskState) { this.state = next; this.listener?.(next) }
  queue() { this.commit(taskReducer(this.state, { type: 'queue' })); return this.state }
  setDraft(value: string) { this.commit(taskReducer(this.state, { type: 'draft', value })); return this.state }
  setScroll(value: number) { this.commit(taskReducer(this.state, { type: 'scroll', value })); return this.state }
  setUnread(value: number) { this.commit(taskReducer(this.state, { type: 'unread', value })); return this.state }
  ingest(value: unknown) {
    if (!validateTaskEvent(value) || value.taskId !== this.taskId) { this.commit({ ...this.state, diagnostics: [...this.state.diagnostics, 'Invalid task event'].slice(-50) }); return false }
    this.commit(taskReducer(this.state, { type: 'event', event: value })); return true
  }
  async replay(fetcher: (url: string) => Promise<ReplayResponse>, base = '/api/codex/task-events', startAfter?: number) {
    let after = typeof startAfter === 'number' && Number.isFinite(startAfter) ? startAfter : -1
    for (let page = 0; page < 100; page += 1) {
      const previousAfter = after
      const response = await fetcher(`${base}?taskId=${encodeURIComponent(this.taskId)}&after=${after}&limit=500`)
      const events = (response.events ?? []).filter(validateTaskEvent)
      const cursorValue = response.next ?? response.cursor
      if (events.length) {
        this.commit(taskReducer(this.state, { type: 'replay', events }))
        const latest = events.reduce((max, event) => Math.max(max, event.sequence), after)
        after = Math.max(latest, Number(cursorValue ?? latest))
      }
      if (!events.length || (response.hasMore !== true && events.length < 500) || (cursorValue !== undefined && Number(cursorValue) <= previousAfter)) break
    }
    return this.state
  }
  connect(factory: EventSourceFactory = (url) => new EventSource(url), url = '/api/codex/events') {
    this.stopped = false
    this.source?.close()
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    const source = factory(url)
    this.source = source
    source.addEventListener('task:event', (event) => {
      try { const packet = JSON.parse((event as MessageEvent).data) as { event?: unknown }; this.ingest(packet.event ?? packet) }
      catch (error) { this.commit({ ...this.state, diagnostics: [...this.state.diagnostics, diagnosticMessage(error)].slice(-50) }) }
    })
    source.onopen = () => { this.attempt = 0 }
    source.onerror = () => {
      source.close()
      if (this.stopped || this.reconnectTimer) return
      const delay = Math.min(8_000, 250 * 2 ** this.attempt)
      this.attempt += 1
      this.reconnectTimer = setTimeout(() => { this.reconnectTimer = undefined; if (!this.stopped) this.connect(factory, url) }, delay)
    }
    return source
  }
  disconnect() { this.stopped = true; if (this.reconnectTimer) clearTimeout(this.reconnectTimer); this.reconnectTimer = undefined; this.source?.close(); this.source = undefined }
}

export class TaskRuntimeRegistry {
  private runtimes = new Map<string, TaskRuntime>()
  private metadata = new Map<string, { unread: number; scrollTop: number; draft: string }>()
  private taskByThread = new Map<string, string>()
  get(taskId: string) {
    let runtime = this.runtimes.get(taskId)
    if (!runtime) {
      runtime = new TaskRuntime(taskId, (state) => this.metadata.set(taskId, { unread: state.unread, scrollTop: state.scrollTop, draft: state.draft }))
      const saved = this.metadata.get(taskId)
      if (saved) { runtime.setUnread(saved.unread); runtime.setScroll(saved.scrollTop); runtime.setDraft(saved.draft) }
      this.runtimes.set(taskId, runtime)
    }
    return runtime
  }
  ingest(event: TaskEvent) {
    if (event.threadId) this.taskByThread.set(event.threadId, event.taskId)
    const runtime = this.get(event.taskId)
    runtime.ingest(event)
    return runtime
  }
  forThread(threadId: string) {
    const taskId = this.taskByThread.get(threadId)
    return taskId ? this.runtimes.get(taskId) : undefined
  }
  entries() { return [...this.runtimes.entries()] }
  remove(taskId: string) { this.runtimes.get(taskId)?.disconnect(); this.runtimes.delete(taskId) }
  clear() { for (const runtime of this.runtimes.values()) runtime.disconnect(); this.runtimes.clear(); this.taskByThread.clear() }
}
